// utils/youtubeUtils.js
//
// YouTube URL parsing, matched to the app's utils/youtubeUtils.js.
//
// The app version also downloads the thumbnail to the device's filesystem,
// because a React Native <Image> wants a local file. A browser does not: it
// can point an <img> straight at img.youtube.com, so the web half is only the
// parsing and the thumbnail-picking — and the one piece the app does not need,
// which is reading a playlist id out of a URL.

/** A video id from any shape of YouTube link, or null. */
export function getYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsed = new URL(url.trim());

    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1, 12) || null;
    }

    if (
      parsed.hostname.includes('youtube.com') ||
      parsed.hostname.includes('music.youtube.com') ||
      parsed.hostname.includes('m.youtube.com')
    ) {
      if (parsed.searchParams.has('v')) return parsed.searchParams.get('v');

      const match = parsed.pathname.match(/\/(embed|shorts|live)\/([a-zA-Z0-9_-]{11})/);
      if (match) return match[2];
    }

    return null;
  } catch {
    return null;
  }
}

export function isValidYouTubeUrl(url) {
  const id = getYouTubeVideoId(url);
  return id !== null && id.length === 11;
}

/**
 * A playlist id from a YouTube URL, or null.
 *
 * Playlist ids are not a fixed length — PL… user playlists, RD… radio mixes,
 * UU…/LL… channel and liked-video lists all differ — so this checks the prefix
 * shape rather than a length.
 */
export function getYouTubePlaylistId(url) {
  if (!url || typeof url !== 'string') return null;

  const raw = url.trim();
  // Someone may paste the bare id rather than a link, which is a reasonable
  // thing to do and used to be rejected.
  if (/^(PL|UU|LL|FL|RD|OL)[A-Za-z0-9_-]{10,}$/.test(raw)) return raw;

  try {
    const parsed = new URL(raw);
    if (!parsed.hostname.includes('youtube.com') && parsed.hostname !== 'youtu.be') return null;
    const list = parsed.searchParams.get('list');
    return list && /^[A-Za-z0-9_-]{12,}$/.test(list) ? list : null;
  } catch {
    return null;
  }
}

export function getYouTubeThumbnailUrl(videoId, quality = 'maxresdefault') {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

export function getYouTubeEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}`;
}

export function getYouTubePlaylistEmbedUrl(playlistId) {
  return `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
}

/**
 * The best thumbnail that actually exists for a video.
 *
 * YouTube does not return 404 for a missing maxresdefault — it serves a 120x90
 * grey placeholder — so "does this URL load" is not the question. The question
 * is whether what loaded is that placeholder, and its width gives it away.
 *
 * @returns {Promise<{url: string, quality: string}>}
 */
export function bestThumbnail(videoId) {
  const qualities = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault'];

  const probe = (quality) =>
    new Promise((resolve) => {
      const url = getYouTubeThumbnailUrl(videoId, quality);
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth > 120 ? { url, quality } : null);
      img.onerror = () => resolve(null);
      img.src = url;
    });

  return qualities
    .reduce(
      (chain, quality) => chain.then((found) => found || probe(quality)),
      Promise.resolve(null),
    )
    // hqdefault always exists for a real video, so this is the floor rather
    // than a failure case.
    .then((found) => found || { url: getYouTubeThumbnailUrl(videoId, 'hqdefault'), quality: 'hqdefault' });
}

/**
 * Validates a URL and gathers what a cover needs from it.
 *
 * @returns {Promise<{ok: true, videoId, originalUrl, thumbnailUrl, thumbnailQuality} | {ok: false, error: string}>}
 */
export async function validateAndExtractYouTubeData(url) {
  const videoId = getYouTubeVideoId(url);
  if (!videoId || videoId.length !== 11) {
    return { ok: false, error: 'That does not look like a YouTube video link.' };
  }

  const { url: thumbnailUrl, quality } = await bestThumbnail(videoId);

  return {
    ok: true,
    videoId,
    originalUrl: url.trim(),
    thumbnailUrl,
    thumbnailQuality: quality,
  };
}
